import React from 'react';
import { FormElement } from '@uplift/types';
import { TextInput, NumberInput } from '@uplift/ui-v2';
import { EMAIL_MATCHES, NUMBERS_MATCHES } from '@uplift/constants';
import { l10nEnService } from '@uplift/services';


export type FormBuilderTextInputProps = {
  element: FormElement;
  type: 'simple' | 'free';
};

export const FormBuilderTextInput: React.VFC<FormBuilderTextInputProps> = ({ element, type }) => {
  const answer = element.formElementAnswers[0];

  const splitValidation = React.useMemo(
    () => element?.validation?.split(',') ?? [ 'none' ],
    [ element?.validation ]
  );

  const validation = React.useMemo(
    () => splitValidation.filter(x => x !== 'required')[0],
    [ splitValidation ]
  );

  const validate = React.useCallback((value: string): string | undefined => {
    const required = splitValidation.includes('required');

    if (!required && !value) {
      return;
    }

    if (required && !value) {
      return 'This field is required';
    }

    if (validation === 'number' && !(NUMBERS_MATCHES.test(value))) {
      return 'Your answer must contain only numbers.';
    }

    if (validation === 'email' && !(EMAIL_MATCHES.test(value))) {
      return l10nEnService.validations.invalidEmail;
    }

    if ((type === 'simple' && answer?.size) && (value?.length > answer?.size)) {
      return `Your answer must be less than ${answer?.size} characters.`;
    }
  }, [ answer?.size, splitValidation, type, validation ]);

  if (validation === 'number') {
    return (
      <NumberInput
        asString
        name={element.formElementId}
        label={answer?.labelText ?? ''}
        helperText={answer?.hintText}
        numberInputProps={{
          isAllowed: answer?.size
            ? ({ value }) => !value || value?.length <= answer?.size
            : undefined
        }}
        fullWidth={type === 'free'}
        validate={validate}
      />
    );
  }

  return (
    <TextInput
      name={element.formElementId}
      label={answer?.labelText ?? ''}
      helperText={answer?.hintText}
      fullWidth={type === 'free'}
      multiline={type === 'free' && answer?.size > 1}
      rows={
        type === 'free' && answer?.size > 1
          ? answer?.size
          : undefined
      }
      inputProps={{
        size: type === 'simple' && answer?.size
          ? answer.size.toString()
          : undefined,
        maxLength: type === 'simple' && answer?.size
          ? answer.size.toString()
          : undefined,
      }}
      validate={validate}
      errorTextFirst
    />
  );
};
